/**
 * Dimensões de agrupamento ("Agrupar por") da carteira: o `modo` da UI, o campo
 * de opções que alimenta o seletor secundário e o nome do query param.
 *
 * ★ `id_cliente` (Membro) só existe no escopo de GRUPO FAMILIAR. Quem controla
 * isso NÃO é esta lista, e sim a presença do campo em
 * `walletAnalysisOptionsFields`: `buildPrimaryDropdownItems` monta o dropdown
 * pela PRESENÇA do campo, e `buildWalletOptionsFields` só emite o campo de
 * membro quando o OPTIONS traz `group_by_available_values.id_cliente` — o que o
 * OPTIONS por cliente não traz.
 *
 * `buildGroupLabelMap` existe porque as dimensões de id (Conta, Membro) mandam
 * um id no query param mas precisam de rótulo na linha agrupada da tabela e nos
 * gráficos; as de texto já são o próprio rótulo.
 */

import {
  ACCOUNT_FIELD_KEY,
  MEMBER_FIELD_KEY,
  buildOptionsFieldLabelMap,
  isLabeledOptionsField,
} from '@modules/consolidator/utils/build-wallet-options-fields/build-wallet-options-fields'

export const WALLET_MODO_CONFIGS = [
  {
    modo: 'tipo',
    optionsFieldKey: 'tipo',
    filterParam: 'tipo',
    label: 'Tipo',
  },
  {
    modo: 'subtipo',
    optionsFieldKey: 'subtipo',
    filterParam: 'subtipo',
    label: 'Subtipo',
  },
  {
    modo: 'classe',
    optionsFieldKey: 'classe',
    filterParam: 'classe',
    label: 'Classe',
  },
  {
    modo: 'subclasse',
    optionsFieldKey: 'subclasse',
    filterParam: 'subclasse',
    label: 'Subclasse',
  },
  {
    modo: 'nome_portfolio',
    optionsFieldKey: 'nome_portfolio',
    filterParam: 'nome_portfolio',
    label: 'Portfólio',
  },
  {
    modo: 'corretora',
    optionsFieldKey: 'corretora',
    filterParam: 'corretora',
    label: 'Instituição',
  },
  {
    modo: 'setor',
    optionsFieldKey: 'setor',
    filterParam: 'setor',
    label: 'Setor',
  },
  {
    modo: 'emissor',
    optionsFieldKey: 'emissor',
    filterParam: 'emissor',
    label: 'Emissor',
  },
  {
    modo: 'tags',
    optionsFieldKey: 'tags',
    filterParam: 'tags',
    label: 'Tags',
  },
  {
    modo: 'id_conta',
    optionsFieldKey: ACCOUNT_FIELD_KEY,
    filterParam: 'id_conta',
    label: 'Conta',
  },
  {
    modo: MEMBER_FIELD_KEY,
    optionsFieldKey: MEMBER_FIELD_KEY,
    filterParam: MEMBER_FIELD_KEY,
    label: 'Membro',
  },
]

export const MODO_TO_OPTIONS_FIELD_KEY = Object.fromEntries(
  WALLET_MODO_CONFIGS.map((c) => [c.modo, c.optionsFieldKey])
)
export const MODO_TO_FILTER_PARAM = Object.fromEntries(
  WALLET_MODO_CONFIGS.map((c) => [c.modo, c.filterParam])
)
export const MODO_TO_LABEL = Object.fromEntries(
  WALLET_MODO_CONFIGS.map((c) => [c.modo, c.label])
)

export const getWalletModoConfig = (modo) =>
  WALLET_MODO_CONFIGS.find((c) => c.modo === modo) || null

export const getWalletModoLabel = (modo) => MODO_TO_LABEL[modo] || null

export const buildPrimaryDropdownItems = (optionsFields) => {
  if (!Array.isArray(optionsFields)) return []
  const fieldsByKey = {}
  for (const field of optionsFields) {
    if (field && field.key) fieldsByKey[field.key] = field
  }
  const items = []
  for (const config of WALLET_MODO_CONFIGS) {
    const field = fieldsByKey[config.optionsFieldKey]
    if (!field) continue
    items.push({
      text: field.label || config.label,
      value: config.modo,
    })
  }
  return items.sort((a, b) => a.text.localeCompare(b.text, 'pt-BR'))
}

export const buildGroupLabelMap = ({ modo, optionsFields } = {}) => {
  const fieldKey = MODO_TO_OPTIONS_FIELD_KEY[modo]
  if (!fieldKey || !isLabeledOptionsField(fieldKey)) return null
  return buildOptionsFieldLabelMap(optionsFields, fieldKey)
}
